import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Icon, type PhxIconName } from '@/shared/ui/icon';

export type StatCardSize = 'md' | 'lg';
export type StatCardTone = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

const TONE_VALUE: Record<StatCardTone, string> = {
  default: 'text-phx-text',
  primary: 'text-phx-primary',
  success: 'text-phx-success',
  warning: 'text-phx-warning',
  danger: 'text-phx-danger',
  info: 'text-phx-info',
};

const VALUE_SIZE: Record<StatCardSize, string> = {
  md: 'text-[18px]',
  lg: 'text-[22px]',
};

export interface StatCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: ReactNode;
  sub?: ReactNode;
  value: ReactNode;
  icon?: PhxIconName;
  tone?: StatCardTone;
  size?: StatCardSize;
  emphasis?: boolean;
}

export function StatCard({
  label,
  sub,
  value,
  icon,
  tone = 'default',
  size = 'md',
  emphasis,
  className,
  ...rest
}: StatCardProps) {
  const valueTone = emphasis ? TONE_VALUE[tone] : TONE_VALUE.default;
  return (
    <div
      className={cn(
        'phx-stat-card gap-phx-2 rounded-phx-md border-phx-border bg-phx-surface px-phx-5 py-phx-4 shadow-phx-xs flex flex-col border',
        className
      )}
      {...rest}
    >
      <div className="gap-phx-2 text-phx-xs text-phx-text-muted flex items-center font-semibold tracking-wider uppercase">
        {icon ? <Icon name={icon} size={11} className="text-phx-text-muted" /> : null}
        <span>{label}</span>
        {sub ? <span className="text-phx-text-faint font-normal">{sub}</span> : null}
      </div>
      <div
        className={cn(
          'phx-stat-card-value leading-tight font-semibold tabular-nums',
          VALUE_SIZE[size],
          valueTone
        )}
      >
        {value}
      </div>
    </div>
  );
}
